import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  SearchableMultiSelect,
  SelectOption,
} from "@/components/ui/searchable-multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useScheduledPlacementTestMutations,
  useScheduledPlacementTests,
} from "@/hooks/use-inboxplacement";
import { useSearchMailboxes } from "@/hooks/use-mailbox";
import { useSearchTemplates } from "@/hooks/use-template";
import { cn } from "@/lib/utils";
import {
  IScheduledPlacementTest,
  PlacementType,
  RecurrenceType,
} from "@/types/inboxPlacements.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, addHours, format } from "date-fns";
import { CalendarIcon, Clock, Edit, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import CustomRecurrenceDialog from "../custom-recurrence/custom-recurrence-dialog";
import { getRecurrenceText } from "../custom-recurrence/custom-recurrence-utils";
import { EmailProviderSelection } from "../email-provider-selection";
import Trash from "../icons/trash";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import { ESPFilter } from "./esp-filter";
import { AuthProvider } from "@/types/common";

interface ScheduleEmailTestProps {
  isOpen: boolean;
  onClose: () => void;
  initialSelectedDomains?: string[];
}

type TabType = "schedule" | "history";
type InitialValueTest =
  | {
      type: "edit";
      initialValues: IScheduledPlacementTest;
    }
  | {
      type: "create";
      initialSelectedDomains: string[];
    }
  | null;

export default function SchedulePlacementTest({
  isOpen,
  onClose,
  initialSelectedDomains,
}: ScheduleEmailTestProps) {
  const [activeTab, setActiveTab] = useState<TabType>("schedule");
  const [initialValueTest, setInitialValueTest] =
    useState<InitialValueTest>(null);

  const { createScheduledTest, isCreating, updateScheduledTest, isUpdating } =
    useScheduledPlacementTestMutations();

  const handleScheduleTest = async (data: z.infer<typeof formSchema>) => {
    // Combine date and time
    const [hours, minutes] = data.startTime.split(":").map(Number);
    const startDateTime = new Date(data.startDate);
    startDateTime.setHours(hours, minutes, 0, 0);

    if (startDateTime.getTime() < Date.now()) {
      toast.error("Start time cannot be in the past");
      return;
    }

    try {
      if (initialValueTest?.type === "edit") {
        await updateScheduledTest({
          // console.log({
          id: initialValueTest.initialValues._id,
          mailbox: data.mailboxes,
          template: data.template,
          type: data.type === "b2c" ? "B2C" : "B2B",
          scheduleType: data.scheduleType,
          recurrenceType: data.recurrenceType,
          startTime: startDateTime,
          customRecurrence:
            data.recurrenceType === "custom"
              ? data.customRecurrence
              : undefined,
        });
      } else {
        await createScheduledTest({
          // console.log({
          mailbox: data.mailboxes,
          template: data.template,
          type: data.type === "b2c" ? "B2C" : "B2B",
          providers: data.providers,
          scheduleType: data.scheduleType,
          espType: data.espType,
          recurrenceType: data.recurrenceType,
          startTime: startDateTime,
          customRecurrence:
            data.recurrenceType === "custom"
              ? data.customRecurrence
              : undefined,
        });
      }

      const eventName =
        initialValueTest?.type === "edit"
          ? "placement_scheduled_test_updated"
          : "placement_scheduled_test_created";

      window.cioanalytics?.track(eventName, {
        count: data.mailboxes.length,
        mailboxes: data.mailboxes,
        recurrenceType: data.recurrenceType,
        scheduleType: data.scheduleType,
        startTimestamp: Math.floor(startDateTime.getTime() / 1000),
      });

      setInitialValueTest(null);
      onClose();
    } catch (error) {
      console.error("Failed to schedule test:", error);
    }
  };

  const handleEditTest = (test: IScheduledPlacementTest) => {
    setInitialValueTest({
      type: "edit",
      initialValues: test,
    });
    setActiveTab("schedule");
  };

  useEffect(() => {
    if (initialSelectedDomains && initialSelectedDomains.length > 0) {
      setInitialValueTest({
        type: "create",
        initialSelectedDomains,
      });
    }
  }, [initialSelectedDomains]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen && !initialValueTest) {
      setActiveTab("schedule");
    }
  }, [isOpen, initialValueTest]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Placement Test</DialogTitle>
          <DialogDescription>
            Set up automated placement tests for your domains
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as TabType)}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="schedule">Schedule New Test</TabsTrigger>
            <TabsTrigger value="history">Scheduled Tests</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" className="space-y-5 pt-4">
            <ScheduleTestForm
              onSubmit={handleScheduleTest}
              initialValues={initialValueTest}
              isLoading={isCreating || isUpdating}
              disabled={false}
              onReset={() => {
                setInitialValueTest(null);
              }}
            />
          </TabsContent>

          <TabsContent value="history" className="pt-4">
            <ScheduledTestsList onEdit={handleEditTest} template={[]} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

const getDefaultValues = (): z.infer<typeof formSchema> => ({
  mailboxes: [],
  espType: AuthProvider.SMTP,
  template: "",
  type: "b2b" as const,
  providers: [],
  scheduleType: "once",
  recurrenceType: "doesnt-repeat",
  startDate: addDays(new Date(), 1),
  startTime: format(addHours(new Date(), 1), "HH:00"),
  customRecurrence: {
    interval: 1,
    unit: "day",
    endType: "never",
  },
});

const setInitialValues = (
  initialValues: InitialValueTest
): z.infer<typeof formSchema> => {
  if (initialValues?.type === "edit") {
    return {
      mailboxes: initialValues.initialValues.mailbox,
      espType: initialValues.initialValues.espType || AuthProvider.SMTP,
      template: initialValues.initialValues.template._id,
      type:
        initialValues.initialValues.type === "B2C"
          ? ("b2c" as const)
          : ("b2b" as const),
      providers: initialValues.initialValues.provider,
      scheduleType: initialValues.initialValues.schedule.type,
      recurrenceType: initialValues.initialValues.schedule.recurrenceType,
      startDate: initialValues.initialValues.schedule.startTime
        ? new Date(initialValues.initialValues.schedule.startTime)
        : addDays(new Date(), 1),
      startTime: initialValues.initialValues.schedule.startTime
        ? format(
            new Date(initialValues.initialValues.schedule.startTime),
            "HH:00"
          )
        : format(addHours(new Date(), 1), "HH:00"),
      customRecurrence: initialValues.initialValues.schedule
        .customRecurrence || {
        interval: 1,
        unit: "day",
        endType: "never",
      },
    };
  }

  const defaultValues = getDefaultValues();

  return {
    ...defaultValues,
    mailboxes: initialValues?.initialSelectedDomains || defaultValues.mailboxes,
  };
};

const formSchema = z.object({
  mailboxes: z
    .array(z.string())
    .min(1, { message: "Please select at least one mailbox" }),
  espType: z.nativeEnum(AuthProvider),
  template: z.string().min(1, { message: "Please select a template" }),
  type: z.enum(["b2b", "b2c"]),
  providers: z
    .array(z.string())
    .min(1, { message: "Please select at least one provider" }),
  scheduleType: z.enum(["once", "recurring"]),
  recurrenceType: z.enum([
    "doesnt-repeat",
    "daily",
    "weekly",
    "monthly-by-day",
    "monthly-by-date",
    "annually",
    "weekdays",
    "custom",
  ]),
  startDate: z.date(),
  startTime: z.string().min(1),
  customRecurrence: z.object({
    interval: z.number(),
    unit: z.enum(["day", "week", "month", "year"]),
    endType: z.enum(["never", "on", "after"]),
    endDate: z.date().optional(),
    occurrences: z.number().optional(),
    weekdays: z.array(z.number()).optional(),
  }),
});

const ScheduleTestForm = ({
  onSubmit,
  initialValues,
  isLoading,
  disabled,
  onReset,
}: {
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  initialValues: InitialValueTest;
  isLoading: boolean;
  disabled: boolean;
  onReset: () => void;
}) => {
  const [searchTermEmail, setSearchTermEmail] = useState("");
  const [searchTermTemplate, setSearchTermTemplate] = useState("");
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: setInitialValues(initialValues),
  });

  const { data: mailboxes, isLoading: mailboxLoading } =
    useSearchMailboxes(searchTermEmail, form.watch("espType"));
  const { data: templates, isLoading: templateLoading } =
    useSearchTemplates(searchTermTemplate);

  const mailboxOptions: SelectOption[] = useMemo(() => {
    return (
      mailboxes?.map((mailbox) => ({
        value: mailbox.email,
        label: mailbox.email,
        icon: authProviderIcons[mailbox.authProvider as AuthProvider]?.icon,
        tooltip:
          authProviderIcons[mailbox.authProvider as AuthProvider]?.tooltip,
      })) || []
    );
  }, [mailboxes]);

  const templateOptions: SelectOption[] = useMemo(() => {
    return (
      templates?.map((template) => ({
        value: template._id,
        label: template.title,
      })) || []
    );
  }, [templates]);

  const recurrenceType = form.watch("recurrenceType");
  const startDate = form.watch("startDate");
  const customRecurrence = form.watch("customRecurrence");

  // console.log(form.watch());

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* ESP Type Selection - Must be selected first */}
          <FormField
            control={form.control}
            name="espType"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Email Service Provider Type</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={(value) => field.onChange(value as AuthProvider)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select ESP type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AuthProvider.SMTP}>SMTP - Standard SMTP Server</SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE}>Google - Gmail/Google Workspace</SelectItem>
                      <SelectItem value={AuthProvider.MICROSOFT}>Microsoft - Outlook/Office 365</SelectItem>
                      <SelectItem value={AuthProvider.AWS_API}>Amazon SES</SelectItem>
                      <SelectItem value={AuthProvider.SENDGRID_API}>SendGrid</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Select the email service provider type to see available mailboxes
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Mailbox Selection - Only show after ESP type is selected */}
          {form.watch("espType") && (
            <FormField
              control={form.control}
              name="mailboxes"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-center justify-between">
                    <FormLabel>Select Mailboxes</FormLabel>

                    <div className="flex items-center gap-2">
                      {/* <ESPFilter
                        value={field.value}
                        onSelect={(value) => {
                          field.onChange(value);
                        }}
                      /> */}

                      <span className="text-xs text-muted-foreground">
                        {/* @ts-ignore - field.value may be undefined */}
                        {(field.value || []).length} of {mailboxOptions?.length} selected
                      </span>

                      {/* @ts-ignore - field.value may be undefined */}
                      {(field.value || []).length !== mailboxOptions.length ? (
                        <Button
                          variant="outline"
                          type="button"
                          size="sm"
                          className="h-6"
                          onClick={() => {
                            field.onChange(
                              mailboxOptions.map((domain) => domain.value)
                            );
                          }}
                        >
                          Select All
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          type="button"
                          size="sm"
                          className="h-6"
                          onClick={() => {
                            field.onChange([]);
                          }}
                        >
                          Deselect All
                        </Button>
                      )}
                    </div>
                  </div>
                  <FormControl>
                    {/* @ts-ignore - field.value may be undefined */}
                    <SearchableMultiSelect
                      // label="Select Connected Mailbox"
                      options={mailboxOptions}
                      selectedValues={field.value || []}
                      onChange={field.onChange}
                      searchTerm={searchTermEmail}
                      setSearchTerm={setSearchTermEmail}
                      placeholder="Select mailboxes..."
                      maxHeight={200}
                      forceOpen={!!searchTermEmail}
                      loading={mailboxLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Show message when no ESP type is selected */}
          {!form.watch("espType") && (
            <div className="p-4 text-center text-muted-foreground border rounded-md">
              Please select an Email Service Provider type above to see available mailboxes
            </div>
          )}

          {/* Rest of the form fields - only show when mailboxes are selected */}
          {form.watch("espType") && form.watch("mailboxes").length > 0 && (
            <>
              <FormField
                control={form.control}
                name="providers"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Email Service Providers</FormLabel>
                    <FormControl>
                      <div className="border rounded-md p-4">
                        <EmailProviderSelection
                          testTypeRecipientRef={form.watch("type")}
                          onProvidersChange={(providers) => {
                            field.onChange(
                              providers.map((provider) => provider.value)
                            );
                          }}
                          onProviderTypeChange={(type) => {
                            form.setValue("type", type);
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="template"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Select Template</FormLabel>
                      <FormControl>
                        <SearchableMultiSelect
                          // label="Select Template"
                          options={templateOptions}
                          selectedValues={field.value ? [field.value] : []}
                          onChange={(values) => field.onChange(values[0] || "")}
                          searchTerm={searchTermTemplate}
                          setSearchTerm={setSearchTermTemplate}
                          placeholder="Select a template"
                          maxHeight={200}
                          loading={templateLoading}
                          optionTruncate
                          singleSelect
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recurrenceType"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Select Recurrence Type</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={(value: RecurrenceType) => {
                            console.log("value", value);
                            field.onChange(value);
                            form.setValue(
                              "scheduleType",
                              value === "doesnt-repeat" ? "once" : "recurring"
                            );
                            if (value === "custom") {
                              console.log("custom");
                              setShowCustomDialog(true);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue>
                              {getRecurrenceText(
                                recurrenceType,
                                customRecurrence,
                                startDate
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="doesnt-repeat">
                              Doesn't repeat
                            </SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">
                              Weekly on {format(startDate, "EEEE")}
                            </SelectItem>
                            <SelectItem value="monthly-by-day">
                              Monthly on the first {format(startDate, "EEEE")}
                            </SelectItem>
                            <SelectItem value="monthly-by-date">
                              Monthly on day {format(startDate, "d")}
                            </SelectItem>
                            <SelectItem value="annually">
                              Annually on {format(startDate, "MMMM d")}
                            </SelectItem>
                            <SelectItem value="weekdays">
                              Every weekday (Monday to Friday)
                            </SelectItem>
                            <SelectItem value="custom">Custom...</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Select Start Date</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal text-sm",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value
                                ? format(field.value, "MMMM d, yyyy")
                                : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => date && field.onChange(date)}
                              initialFocus
                              disabled={(date) => date < new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Select Start Time</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="w-full">
                            <div className="flex items-center">
                              <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <ScrollArea className="h-[200px]">
                              {Array.from({ length: 24 * 4 }, (_, i) => {
                                const hour = Math.floor(i / 4);
                                const minute = (i % 4) * 15;
                                const time = `${hour
                                  .toString()
                                  .padStart(2, "0")}:${minute
                                  .toString()
                                  .padStart(2, "0")}`;
                                const period = hour >= 12 ? "PM" : "AM";
                                const displayHour =
                                  hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                return (
                                  <SelectItem key={time} value={time}>
                                    {`${displayHour}:${minute
                                      .toString()
                                      .padStart(2, "0")} ${period}`}
                                  </SelectItem>
                                );
                              })}
                            </ScrollArea>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}
        </form>
      </Form>

      <DialogFooter className="mt-4">
        <Button
          variant="outline"
          onClick={() => {
            form.reset(getDefaultValues());
            onReset();
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={isLoading || disabled}
        >
          {isLoading
            ? "Scheduling..."
            : initialValues?.type === "edit"
            ? "Update Test"
            : "Schedule Test"}
        </Button>
      </DialogFooter>

      <CustomRecurrenceDialog
        open={showCustomDialog}
        onClose={() => setShowCustomDialog(false)}
        customRecurrence={customRecurrence}
        setCustomRecurrence={(customRecurrence) => {
          form.setValue("customRecurrence", customRecurrence);
        }}
        startDate={startDate}
      />
    </div>
  );
};

const ScheduledTestsList = ({
  onEdit,
  template,
}: {
  onEdit: (test: IScheduledPlacementTest) => void;
  template: IScheduledPlacementTest[];
}) => {
  const { deleteScheduledTest, isDeleting } =
    useScheduledPlacementTestMutations();

  const { data: scheduledTests, isLoading: isLoadingTests } =
    useScheduledPlacementTests();

  const handleDeleteTest = async (id: string) => {
    try {
      await deleteScheduledTest(id);
    } catch (error) {
      console.error("Failed to delete test:", error);
    }
  };

  return (
    <ScrollArea className="h-[450px] pr-4">
      {isLoadingTests ? (
        <div className="text-center py-2">Loading scheduled tests...</div>
      ) : !scheduledTests ||
        !Array.isArray(scheduledTests) ||
        scheduledTests.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No scheduled tests found
        </div>
      ) : (
        <div className="space-y-4">
          {scheduledTests.map((test: IScheduledPlacementTest) => (
            <div key={test._id} className="border rounded-lg p-4 space-y-3 ">
              <div className="flex justify-between items-start">
                <h4 className="font-medium">
                  {test.mailbox?.length > 1
                    ? `${test.mailbox[0]} +${test.mailbox.length - 1} more`
                    : test.mailbox[0]}
                </h4>

                <Badge
                  variant="soft"
                  color={
                    test.status === "active"
                      ? "blue"
                      : test.status === "completed"
                      ? "green"
                      : test.status === "failed"
                      ? "red"
                      : "default"
                  }
                  className="ml-2 capitalize"
                >
                  {test.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-wrap gap-1 col-span-full">
                  {test.mailbox.map((domain: string) => (
                    <Badge
                      key={domain}
                      variant="outline"
                      className="font-normal"
                    >
                      {domain}
                    </Badge>
                  ))}
                </div>

                <div className="text-sm space-y-1">
                  <span className="font-medium">Recurrence: </span>
                  <div className="text-sm text-muted-foreground">
                    {getRecurrenceText(
                      test.schedule.recurrenceType,
                      test.schedule.customRecurrence,
                      new Date(test.schedule.startTime)
                    )}
                  </div>
                </div>

                {/* Add Test Type information */}
                <div className="text-sm space-y-1">
                  <span className="font-medium">Type: </span>
                  <div>
                    {[PlacementType.B2B, PlacementType.B2C].includes(
                      test.type as PlacementType
                    ) ? (
                      <div
                        className={cn(
                          "px-2 py-1 w-fit rounded-full text-center text-xs font-semibold",
                          test.type === PlacementType.B2B
                            ? "bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-500"
                            : "bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-500"
                        )}
                      >
                        {test.type}
                      </div>
                    ) : (
                      test.type || "Not specified"
                    )}
                  </div>
                </div>

                {test.provider && test.provider.length > 0 && (
                  <div className="text-sm col-span-full space-y-1">
                    <span className="font-medium">Providers:</span>
                    <div className="flex flex-wrap gap-1 text-muted-foreground">
                      {test.provider?.map((provider: string) => (
                        <Badge
                          key={provider}
                          variant="secondary"
                          className="text-xs font-normal"
                        >
                          {provider}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-sm space-y-1">
                  <span className="font-medium">Template: </span>
                  <div className="text-sm text-muted-foreground">
                    {test.template.title}
                  </div>
                </div>

                <div className="text-sm space-y-1">
                  <span className="font-medium">Started At: </span>
                  <div className="text-sm text-muted-foreground">
                    {format(
                      new Date(test.schedule.startTime),
                      "MMMM d, yyyy h:mm a"
                    )}
                  </div>
                </div>

                <div className="text-sm space-y-1">
                  <span className="font-medium">Last Run: </span>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(test.lastRun), "MMMM d, yyyy h:mm a")}
                  </div>
                </div>
                <div className="text-sm space-y-1">
                  <span className="font-medium">Next Run: </span>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(test.nextRun), "MMMM d, yyyy h:mm a")}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(test)}
                >
                  <Edit className="h-3.5 w-3.5 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteTest(test._id)}
                  disabled={isDeleting}
                  className="delete-button group"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  );
};
